import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { usePortfolio } from 'querys/portfolio'
import Wrapper from 'common/wrapper'
import { TransparentButton } from 'common/button'
import { FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa'
import { Typography } from 'common/typography'

const PortfolioTestimoniesStyled = styled.section`
  margin-block-end: 80px;
  .slider-container {
    display: flex;
    overflow: hidden;
    scroll-behavior: smooth;
  }
  .slider-slide {
    padding-block-start: 80px;
    min-width: 100%;
    text-align: center;
  }
  .slider-photo-list {
    margin-block-start: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 32px;
    max-width: calc(100vw - 136px);
    margin: 40px auto 0;
    overflow-x: auto;
    padding-inline-start: 50px;
    box-sizing: border-box;
    scroll-behavior: smooth;
  }
  .slider-photo {
    border-radius: 50%;
    overflow: hidden;
    height: fit-content;
    min-width: 40px;
    &.focus  {
      min-width: 80px;
      img {
        width: 80px;
        height: 80px;
      }
    }
  }
  .role {
    color: var(--turquoise);
  }
  .testimony {
    color: var(--neutral40);
    margin-block-start: 16px;
  }
  .testimonies-container {
    position: relative;
  }
  .arrow-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    inline-size: 44px;
    block-size: 44px;
    background: var(--lightBlue);
    position: absolute;
    bottom: 16px;
    &.left {
      left: 0;
    }
    &.right {
      right: 0;
    }
  }
  @media (min-width: 768px) {
    margin-block-end: 140px;
    .slider-photo-list {
      max-width: auto;
      margin-block-start: 80px;
      padding: 0;
    }
    .arrow-btn {
      bottom: calc(50% - 25px);
      bottom: calc(50% - 25px);
      &.left {
        left: -44px;
      }
      &.right {
        right: -44px;
      }
    }
    .slider-slide {
      padding-block-start: 140px;
    }
    .testimony {
      font-size: 22px;
      line-height: 38px;
      font-weight: 400;
    }
    .slider-photo {
      border-radius: 50%;
      overflow: hidden;
      height: fit-content;
      img {
        width: 64px;
        height: 64px;
      }
      &.focus img {
        width: 126px;
        height: 126px;
      }
    }
  }
  @media (min-width: 1024px) {
    .arrow-btn {
      &.left {
        left: -84px;
      }
      &.right {
        right: -84px;
      }
    }
  }
`

function Photo({
  uuid, name, photo, active, setActive,
}) {
  return (
    <a
      onClick={() => setActive(uuid)}
      href={`#${uuid}`}
      className={`slider-photo ${active ? 'focus' : ''}`}
      id={`photo-${uuid}`}
      key={`photo-${uuid}`}
    >
      <img src={photo} alt={name} width="40" height="40" />
    </a>
  )
}

function PortfolioTestimonies() {
  const sliderRef = useRef(null)
  const photosRef = useRef(null)
  const [previous, setPrevious] = useState(null)
  const [next, setNext] = useState(null)
  const { data: { testimonies } } = usePortfolio()
  const [active, setActive] = useState()
  const { asPath } = useRouter()

  useEffect(() => {
    let activeUuid = asPath.length > 50 ? asPath.slice(-36) : null
    if (!activeUuid) {
      activeUuid = testimonies[0]?.uuid
    }
    if (testimonies.length === 2) {
      setNext(testimonies[1].uuid)
    }
    if (testimonies.length >= 3) {
      const center = Math.ceil(testimonies.length / 2) - 1
      activeUuid = testimonies[center].uuid
      if (sliderRef.current) {
        const position = sliderRef.current.querySelector(`#${CSS.escape(activeUuid)}`).offsetLeft
        sliderRef.current.scrollTo(position, 0)
      }

      const preceding = testimonies[center - 1].uuid
      const incoming = testimonies[center + 1].uuid

      setPrevious(preceding)
      setNext(incoming)
    }
    setActive(activeUuid)
  }, [])

  const setPreviousAndNextFromId = (uuid, scroll) => {
    if (scroll) {
      const testimonyPosition = sliderRef.current.querySelector(`#${CSS.escape(uuid)}`).offsetLeft
      sliderRef.current.scrollTo(testimonyPosition, 0)
    }

    setActive(uuid)
    const actual = testimonies.findIndex((item) => item.uuid === uuid)
    const preceding = testimonies[actual - 1]?.uuid || null
    const incoming = testimonies[actual + 1]?.uuid || null
    setPrevious(preceding)
    setNext(incoming)

    if (window.matchMedia('(max-width: 768px)').matches) {
      const position = photosRef.current.querySelector(`#photo-${CSS.escape(uuid)}`).offsetLeft
      photosRef.current.scrollTo(position - 52, 0)
    }
  }

  if (testimonies.length === 0) return null

  return (
    <PortfolioTestimoniesStyled aria-label="testimonios" id="testimonios">
      <Wrapper maxWidth={800}>
        <div className="testimonies-container">
          <ul className="slider-container" ref={sliderRef}>
            {testimonies.map(({
              uuid, name, role, testimony,
            }) => (
              <li key={uuid} className="slider-slide" id={uuid}>
                <Typography as="h4" small="headline6" large="headline5" className="name">
                  {name}
                </Typography>
                <Typography className="role" small="p2Regular" large="regular">{role}</Typography>
                <Typography className="testimony" small="regular" large="headline5">
                  {testimony}
                </Typography>
              </li>
            ))}
          </ul>
          <nav className="slider-photo-list" ref={photosRef}>
            {testimonies.map((testimony) => (
              <Photo
                key={testimony.uuid}
                active={active === testimony.uuid}
                setActive={setPreviousAndNextFromId}
                {...testimony}
              />
            ))}
          </nav>
          {previous ? (
            <TransparentButton
              className="arrow-btn left"
              onClick={() => setPreviousAndNextFromId(previous, true)}
            >
              <FaAngleDoubleLeft size="24" color="var(--neutral110)" />
            </TransparentButton>
          ) : null}
          {next ? (
            <TransparentButton
              className="arrow-btn right"
              onClick={() => setPreviousAndNextFromId(next, true)}
            >
              <FaAngleDoubleRight size="24" color="var(--neutral110)" />
            </TransparentButton>
          ) : null}
        </div>
      </Wrapper>
    </PortfolioTestimoniesStyled>
  )
}

export default PortfolioTestimonies
